import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APIService } from 'src/app/core/api/general.service';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { DashboardService } from '../../dashboard/services/dashboard.service';
import { ItineraryService } from '../../search-result/services/itinerary.service';
import { PnrInfo, SearchForm } from 'src/app/core/models/itinerary.model';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import { ReissueService } from 'src/app/reissue/services/reissue.service';
import { ReportLogService } from 'src/app/shared/services/report-log.service';
import { OptionType } from 'src/app/core/enums/enums';
import { DashboardComponent } from 'src/app/dashboard/containers/dashboard/dashboard.component';
import { PnrState } from '../reducers/pnr.reducer';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot
} from '@angular/router';

function getInfo(
  form: SearchForm,
  prevTransactionID: string,
  api: APIService
): Observable<PnrInfo> {
  return form.searchType == 'tkt'
    ? api.getPnrInfoByTKT(form)
    : api.getPnrInfo(form.pnr, form.gds, form.bookingCode, prevTransactionID, false);
}

function loadPnrInfo(
  form: SearchForm,
  localSearchResultState: string,
  router: Router,
  api: APIService,
  reissueService: ReissueService,
  layoutService: LayoutService,
  itineraryService: ItineraryService,
  dashboardService: DashboardService,
  voluntaryChangeService: VoluntaryChangeService,
  reportLog: ReportLogService
) {
  const prevTransactionID = (
    JSON.parse(
      window.localStorage.getItem(localSearchResultState)
        ? window.localStorage.getItem(localSearchResultState)
        : ''
    ) as PnrState
  )?.pnrInfo?.transactionId;

  if (form.gds === null) {
    router.navigate(['/dashboard']);
    return of(false);
  }

  return getInfo(form, prevTransactionID, api).pipe(
    map((response) => {
      reissueService.clearReissue();
      layoutService.dispatchChangeBreadcrumb(response.code, 'itinerary');
      itineraryService.dispatcPnrInfoSuccess(response);
      dashboardService.dispatchLoadPnrSuccess();
      voluntaryChangeService.dispatchStoreLoadingPenalties();
      api
        .getPenaltiesFareBasis({
          ...form,
          passengers: response.passengers.map((passenger) => ({
            ticketNumber: passenger?.ticket?.number
          }))
        })
        .subscribe((penaltyFareBasis) => {
          voluntaryChangeService.dispatchStorePenaltiesRequestExchange(penaltyFareBasis);
        });
      DashboardComponent.isFirstSearch;
      DashboardComponent.isFirstSearch = false;
      return !!response;
    }),
    catchError((response) => {
      dashboardService.dispatchLoadPnrFail(response.error);
      router.url.includes('integrations')
        ? router.navigate(['integrations/error'])
        : router.navigate(['/dashboard']);
      DashboardComponent.isFirstSearch && reportLog.Search(OptionType.NA);
      DashboardComponent.isFirstSearch = false;
      return of(false);
    })
  );
}

export const ItineraryGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const api = inject(APIService);
  const itineraryService = inject(ItineraryService);
  const dashboardService = inject(DashboardService);
  const voluntaryChangeService = inject(VoluntaryChangeService);
  const layoutService = inject(LayoutService);
  const router = inject(Router);
  const reissueService = inject(ReissueService);
  const reportLog = inject(ReportLogService);

  const localSearchResultState = 'searchResult';

  return loadPnrInfo(
    { ...route.queryParams } as SearchForm,
    localSearchResultState,
    router,
    api,
    reissueService,
    layoutService,
    itineraryService,
    dashboardService,
    voluntaryChangeService,
    reportLog
  );
};
